// #region Imports
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { PROFILE_RAIL_WIDTH } from "work/staffing/_views/staffing-picker-stage";
// #endregion

/** What the control that opens the rail back into the whole profile is called. */
export const EXPAND_THE_PROFILE = "Show the whole profile";

// #region Styled Components
const Rail = styled("div")(({ theme }) => ({
  alignItems: "center",
  borderRight: `1px solid ${theme.palette.divider}`,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  height: "100%",
  maxWidth: PROFILE_RAIL_WIDTH,
  minWidth: 0,
  overflow: "hidden",
  padding: theme.spacing(2, 1.5),
  textAlign: "center",
  width: "100%",
}));

const RailAvatar = styled(Avatar)(({ theme }) => ({
  height: theme.spacing(7),
  width: theme.spacing(7),
}));

/**
 * The name, on as many lines as it takes and no more than two.
 *
 * A rail is the profile reduced to who this is, so the name is the one thing on it that must not be
 * dropped -- but a name long enough to take four lines would push the company off the rail instead.
 */
const RailName = styled(Typography)(() => ({
  display: "-webkit-box",
  overflow: "hidden",
  overflowWrap: "anywhere",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  width: "100%",
}));

const RailCompany = styled(Typography)(() => ({
  display: "-webkit-box",
  overflow: "hidden",
  overflowWrap: "anywhere",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  width: "100%",
}));
// #endregion

export type StaffingCandidateRailProps = {
  /** Who is being staffed, in the words the profile's own header uses. */
  name: string;
  /** Where they work, where the profile knows it. */
  companyName?: string;
  /** Their photograph, where there is one. */
  avatarUrl?: string;
  /** Open the rail back into the whole profile, which pushes the picker aside to make room. */
  onExpand: () => void;
};

/**
 * The profile reduced to who it is, for the widths that cannot hold both it and the table.
 *
 * **It stands in for the profile rather than over it.** The dialog gives the rail the column the
 * profile had, and the profile is not drawn -- not covered, not scrolled away, and not unmounted,
 * so opening it back up returns exactly the profile the reader left.
 *
 * Avatar, name and company, and nothing else: the question a reader is answering with the table
 * beside them is "is this the right piece of work for this person", and those three are what that
 * question needs. Everything else about them is one press away.
 */
export default function StaffingCandidateRail(props: Readonly<StaffingCandidateRailProps>) {
  return (
    <Rail>
      <RailAvatar src={props.avatarUrl} alt="" />
      <RailName variant="subtitle2">{props.name}</RailName>
      {props.companyName && (
        <RailCompany color="text.secondary" variant="caption">
          {props.companyName}
        </RailCompany>
      )}
      <Tooltip title={EXPAND_THE_PROFILE} placement="right">
        <span>
          <IconButton aria-label={EXPAND_THE_PROFILE} size="small" onClick={props.onExpand}>
            <ChevronRightIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Rail>
  );
}
